import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import DatePicker from "react-datepicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  gatewayStatusAction,
  getProjectWiseCallsAction,
  getGatewayQueueLeftAction,
} from "../Admin/Store/admin.action";
import {
  saveCallSettingsAction,
  getAllCallSettingAction,
} from "../ProjectSection/CallSettings/Store/callSetting.action";
import {
  swalSuccessMessage,
  swalErrorMessage,
  swalConfirmationMessage,
} from "../../../Constant/swalMessage";
import { shortenString } from "../../../Constant/helper";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { dashboardStyles } from "../../../assets/MUIStyles/dashboard";
import { ReactComponent as RefreshIcon } from "../../../assets/icons/refresh.svg";
import DynamicTable from "../CommonComponents/Table/DynamicTable";
import SearchableSelect from "../Common/Select/SearchableSelect";
import { LoadingDots } from "../CommonComponents/Common";

const getStatusColor = (status: number) => {
  switch (status) {
    case 200:
      return { color: "#e57373", bg: "#fdecea" };
    case 404:
      return { color: "#6d4c41", bg: "#efebe9" };
    case 479:
      return { color: "#673ab7", bg: "#ede7f6" };
    case 480:
      return { color: "#ba68c8", bg: "#f3e5f5" };
    case 486:
      return { color: "#7986cb", bg: "#e8eaf6" };
    default:
      return { color: "#90a4ae", bg: "#eceff1" };
  }
};

const sumDisposition = (disposition: any[]) =>
  (disposition || []).reduce((sum, d) => sum + (d?.count || 0), 0);

// Pure builder: merge the gateway_details response with configured gateways.
const buildGatewayRows = (res: any, gateways: any[]) => {
  const apiList = Array.isArray(res) ? res : [];
  const byValue = Object.fromEntries(
    apiList.map((g: any) => [g?.gateway_name, g])
  );
  const configured = gateways || [];
  const seen = new Set();

  const merged = configured.map((gw: any) => {
    seen.add(gw?.value);
    const disposition = byValue[gw?.value]?.disposition || [];
    return {
      gatewayValue: gw?.value,
      gatewayName: gw?.name || gw?.value,
      totalCalls: sumDisposition(disposition),
      disposition,
    };
  });

  apiList.forEach((g: any) => {
    if (!seen.has(g?.gateway_name)) {
      const disposition = g?.disposition || [];
      merged.push({
        gatewayValue: g?.gateway_name,
        gatewayName: g?.gateway_name,
        totalCalls: sumDisposition(disposition),
        disposition,
      });
    }
  });

  merged.sort((a: any, b: any) => b.totalCalls - a.totalCalls);
  return merged;
};

function GatewayPerformance() {
  const dispatch = useDispatch();
  const reducer: any = useSelector((state) => state);
  const {
    AccountReducer: { constantData },
  } = reducer;

  // Default to yesterday
  const [selectedDate, setSelectedDate] = useState(
    moment().subtract(1, "day").toDate()
  );
  const [viewMode, setViewMode] = useState<"gateway" | "project">("gateway");
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  // Gateway view (cached by date via gatewayFetchedDate)
  const [gatewayRawRes, setGatewayRawRes] = useState<any[]>([]);
  const [queueLeftById, setQueueLeftById] = useState<Record<string, any>>({});
  const [gatewayFetchedDate, setGatewayFetchedDate] = useState<string>("");
  const [selectedGatewayValue, setSelectedGatewayValue] = useState<
    string | null
  >(null);
  const [gatewaySearch, setGatewaySearch] = useState("");

  // Project view — fast list cached once, counts cached by date
  const [projectRows, setProjectRows] = useState<any[]>([]);
  const [projectListFetched, setProjectListFetched] = useState(false);
  const [countsById, setCountsById] = useState<Record<string, any>>({});
  const [countsFetchedDate, setCountsFetchedDate] = useState<string>("");
  const [countsLoading, setCountsLoading] = useState(false);
  const [savingProjectId, setSavingProjectId] = useState<string | null>(null);
  const [projectSearch, setProjectSearch] = useState("");
  // Staged gateway selection per project (applied only on Save)
  const [pendingGatewayById, setPendingGatewayById] = useState<
    Record<string, string>
  >({});
  const [projectPage, setProjectPage] = useState(1);

  const gatewayNameFor = (value: string) =>
    (constantData?.gateways || []).find((g: any) => g?.value === value)?.name ||
    value ||
    "-";

  // ── Fetchers ──────────────────────────────────────────────────────────────
  const fetchGatewayData = async (date: Date, spin: boolean) => {
    const fmt = moment(date).format("DD-MM-YYYY");
    if (spin) setLoading(true);
    try {
      const [res, queueResp]: any = await Promise.all([
        dispatch(gatewayStatusAction({ date: fmt })),
        dispatch(getGatewayQueueLeftAction({ date: fmt })),
      ]);
      setGatewayRawRes(Array.isArray(res) ? res : []);
      setQueueLeftById(queueResp?.data?.gateways_queue_count || {});
    } catch (err) {
      setGatewayRawRes([]);
      setQueueLeftById({});
    }
    setGatewayFetchedDate(fmt);
    setLoading(false);
  };

  const fetchProjectList = async (spin: boolean) => {
    if (spin) setLoading(true);
    try {
      const res: any = await dispatch(getAllCallSettingAction());
      const list = (Array.isArray(res) ? res : [])
        .map((item: any) => ({
          projectId: item?.projectId?._id,
          projectName: item?.projectId?.projectName || "-",
          gateway: item?.gateway || "",
          gatewayName: gatewayNameFor(item?.gateway),
        }))
        .filter((r: any) => r.projectId);
      setProjectRows(list);
    } catch (err) {
      setProjectRows([]);
    }
    setProjectListFetched(true);
    setLoading(false);
  };

  const fetchProjectCounts = async (date: Date) => {
    const fmt = moment(date).format("DD-MM-YYYY");
    setCountsLoading(true);
    try {
      const resp: any = await dispatch(getProjectWiseCallsAction({ date: fmt }));
      const results = resp?.data?.results || [];
      setCountsById(
        Object.fromEntries(
          results.map((r: any) => [
            r?.projectId,
            {
              totalCalls: r?.totalCalls || 0,
              connectedCalls: r?.connectedCalls || 0,
            },
          ])
        )
      );
    } catch (err) {
      setCountsById({});
    }
    setCountsFetchedDate(fmt);
    setCountsLoading(false);
  };

  // ── Effects (fetch only when not already cached for this date) ──────────────
  useEffect(() => {
    if (viewMode !== "gateway") return;
    const fmt = moment(selectedDate).format("DD-MM-YYYY");
    if (gatewayFetchedDate === fmt) return;
    fetchGatewayData(selectedDate, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedDate]);

  useEffect(() => {
    if (viewMode !== "project") return;
    if (!projectListFetched) fetchProjectList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== "project") return;
    const fmt = moment(selectedDate).format("DD-MM-YYYY");
    if (countsFetchedDate === fmt) return;
    fetchProjectCounts(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedDate]);

  // Reset to first page when the project filter or view changes
  useEffect(() => {
    setProjectPage(1);
  }, [projectSearch, viewMode]);

  const handleSelectDate = (date: any) => setSelectedDate(date);

  const handleViewChange = (_: any, value: "gateway" | "project" | null) => {
    if (value) setViewMode(value);
  };

  const applyRefresh = async () => {
    setRefreshLoading(true);
    if (viewMode === "gateway") {
      await fetchGatewayData(selectedDate, false);
    } else {
      await fetchProjectList(false);
      await fetchProjectCounts(selectedDate);
    }
    setRefreshLoading(false);
  };

  // ── Gateway change (Call Settings API, with confirmation) ───────────────────
  const saveGateway = async (row: any, newGateway: string) => {
    setSavingProjectId(row?.projectId);
    try {
      const status: any = await dispatch(
        saveCallSettingsAction({
          projectId: row?.projectId,
          gateway: newGateway,
        })
      );
      if (status === 200) {
        setProjectRows((prev) =>
          prev.map((r) =>
            r.projectId === row.projectId
              ? {
                  ...r,
                  gateway: newGateway,
                  gatewayName: gatewayNameFor(newGateway),
                }
              : r
          )
        );
        swalSuccessMessage("Gateway updated");
      }
    } catch (err) {
      swalErrorMessage("Failed to update gateway");
    }
    setSavingProjectId(null);
  };

  const setPendingGateway = (projectId: string, value: string) =>
    setPendingGatewayById((prev) => ({ ...prev, [projectId]: value }));

  const handleSaveGateway = (row: any) => {
    const pending = pendingGatewayById[row?.projectId] ?? row?.gateway;
    if (!pending || pending === row?.gateway) return;
    const newName = gatewayNameFor(pending);
    swalConfirmationMessage(
      `Change gateway for "${row?.projectName}" to "${newName}"?`,
      "Change Gateway",
      () => {},
      () => saveGateway(row, pending),
      "Change Gateway?"
    );
  };

  // ── Derived data ────────────────────────────────────────────────────────────
  const gatewayRows = useMemo(
    () => buildGatewayRows(gatewayRawRes, constantData?.gateways),
    [gatewayRawRes, constantData]
  );
  const grandTotal = useMemo(
    () => gatewayRows.reduce((s: number, r: any) => s + r.totalCalls, 0),
    [gatewayRows]
  );
  const filteredGatewayRows = useMemo(() => {
    const q = gatewaySearch.trim().toLowerCase();
    if (!q) return gatewayRows;
    return gatewayRows.filter(
      (r: any) =>
        (r.gatewayName || "").toLowerCase().includes(q) ||
        (r.gatewayValue || "").toLowerCase().includes(q)
    );
  }, [gatewayRows, gatewaySearch]);

  const selectedGatewayRow = selectedGatewayValue
    ? gatewayRows.find((r: any) => r.gatewayValue === selectedGatewayValue) ||
      null
    : null;

  const projectDisplayRows = useMemo(() => {
    const merged = projectRows.map((r) => {
      const c = countsById[r.projectId];
      return {
        ...r,
        totalCalls: c ? c.totalCalls : countsLoading ? undefined : 0,
        connectedCalls: c ? c.connectedCalls : countsLoading ? undefined : 0,
      };
    });
    if (!countsLoading) {
      merged.sort((a, b) => (b.totalCalls || 0) - (a.totalCalls || 0));
    }
    return merged;
  }, [projectRows, countsById, countsLoading]);

  const filteredProjectRows = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return projectDisplayRows;
    return projectDisplayRows.filter(
      (r: any) =>
        (r.projectName || "").toLowerCase().includes(q) ||
        (r.projectId || "").toLowerCase().includes(q)
    );
  }, [projectDisplayRows, projectSearch]);

  const gatewayOptions = useMemo(
    () =>
      (constantData?.gateways || []).map((g: any) => ({
        label: g?.name,
        value: g?.value,
      })),
    [constantData]
  );

  // Client-side paginate so we only mount one page of gateway dropdowns at a time
  const projectTotalPages = Math.max(
    1,
    Math.ceil(filteredProjectRows.length / 50)
  );
  const pagedProjectRows = filteredProjectRows.slice(
    (projectPage - 1) * 50,
    projectPage * 50
  );

  const projectTotalCalls = Object.values(countsById).reduce(
    (s: number, c: any) => s + (c?.totalCalls || 0),
    0
  );
  const projectConnectedCalls = Object.values(countsById).reduce(
    (s: number, c: any) => s + (c?.connectedCalls || 0),
    0
  );

  const dispositions = (selectedGatewayRow?.disposition || []).map(
    (item: any) => {
      const { color, bg } = getStatusColor(Number(item?.code));
      return {
        status: item?.code,
        disposition: item?.status,
        calls: item?.count,
        percent: item?.percentage,
        color,
        bg,
      };
    }
  );

  const searchField = (
    value: string,
    onChange: (v: string) => void,
    placeholder: string
  ) => (
    <TextField
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      sx={{ width: { xs: "100%", sm: 280 }, mb: 1.5 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" sx={{ color: "#888" }} />
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Box className="my-2">
      <Box className="flex-between">
        <Box className="flex-align-center" gap={1}>
          <Typography className="font-20-grey my-2">
            Gateway Performance
          </Typography>
          {viewMode === "gateway" ? (
            <Typography
              className="font-14-lightgrey"
              component="span"
              sx={commonStyles.textInfo}
            >
              Total calls (all gateways){" "}
              <span className="font-20-700-lightgrey">{grandTotal || 0}</span>
            </Typography>
          ) : (
            <Typography
              className="font-14-lightgrey"
              component="span"
              sx={commonStyles.textInfo}
            >
              Total calls{" "}
              <span className="font-20-700-lightgrey">
                {countsLoading ? <LoadingDots font="16px" /> : projectTotalCalls || 0}
              </span>{" "}
              &nbsp;|&nbsp; Connected{" "}
              <span className="font-20-700-lightgrey">
                {countsLoading ? (
                  <LoadingDots font="16px" />
                ) : (
                  projectConnectedCalls || 0
                )}
              </span>
            </Typography>
          )}
        </Box>
        <IconButton
          title="Refresh"
          onClick={applyRefresh}
          sx={{
            ...commonStyles.refreshButtonStyle,
            ...dashboardStyles.refreshAnimation(refreshLoading),
          }}
          disabled={refreshLoading}
        >
          <RefreshIcon className="refresh-icon" height={14} width={14} />
        </IconButton>
      </Box>

      <Box
        className="flex-align-center"
        sx={{
          flexWrap: { xs: "wrap", sm: "nowrap" },
          justifyContent: "space-between",
          gap: 2,
          py: 2,
        }}
      >
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={handleViewChange}
        >
          <ToggleButton value="gateway" sx={{ textTransform: "none", px: 2 }}>
            By Gateway
          </ToggleButton>
          <ToggleButton value="project" sx={{ textTransform: "none", px: 2 }}>
            By Project
          </ToggleButton>
        </ToggleButtonGroup>

        <Box>
          <Typography className="font-14-grey mb-1">Select Date</Typography>
          <Box className="date-container-2">
            <DatePicker
              selected={selectedDate}
              onChange={handleSelectDate}
              dateFormat="dd-MM-yyyy"
              placeholderText="Select a date"
              className="custom-date-input"
              maxDate={new Date()}
            />
            <CalendarTodayIcon fontSize="small" sx={{ color: "#666" }} />
          </Box>
        </Box>
      </Box>

      {viewMode === "gateway" ? (
        <>
          {searchField(gatewaySearch, setGatewaySearch, "Search gateway…")}
          <DynamicTable
            columns={[
              { label: "Gateway", field: "gatewayName" },
              { label: "Gateway Value", field: "gatewayValue" },
              {
                label: "Total Calls",
                field: "totalCalls",
                render: (row: any) => (
                  <span className="font-13-600-grey">
                    {row?.totalCalls ?? 0}
                  </span>
                ),
              },
              {
                label: "Calls Left in Queue",
                render: (row: any) => {
                  const left = queueLeftById[row?.gatewayValue];
                  return left === undefined ? (
                    <span className="font-13-500">—</span>
                  ) : (
                    <div
                      style={{
                        background: left > 0 ? "#F59E0B" : "#E5E7EB",
                        color: left > 0 ? "#fff" : "#374151",
                        padding: "4px 10px",
                        borderRadius: 4,
                        display: "inline-block",
                      }}
                    >
                      {left}
                    </div>
                  );
                },
              },
              {
                label: "Dispositions",
                render: (row: any) => (
                  <IconButton
                    size="small"
                    title="View disposition summary"
                    onClick={() => setSelectedGatewayValue(row?.gatewayValue)}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                ),
              },
            ]}
            data={filteredGatewayRows}
            loading={loading}
            rowCount={filteredGatewayRows?.length}
            showPagination={false}
          />

          <Dialog
            open={!!selectedGatewayRow}
            onClose={() => setSelectedGatewayValue(null)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ pr: 6 }}>
              <Typography component="span" className="font-16-600-grey">
                {selectedGatewayRow?.gatewayName} — Disposition Summary (
                {selectedGatewayRow?.totalCalls || 0} calls)
              </Typography>
              <IconButton
                onClick={() => setSelectedGatewayValue(null)}
                sx={{ position: "absolute", right: 8, top: 8, color: "#666" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  mx: "auto",
                  border: "1px solid #e0e0e0",
                  ...commonStyles.hideScroll,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
                      <TableCell className="font-13-500">STATUS</TableCell>
                      <TableCell className="font-13-500">DISPOSITION</TableCell>
                      <TableCell className="font-13-500">CALLS</TableCell>
                      <TableCell className="font-13-500">%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dispositions?.length > 0 ? (
                      dispositions.map((row: any, idx: number) => (
                        <TableRow
                          key={idx}
                          sx={{ "& td": { borderBottom: "none" } }}
                        >
                          <TableCell className="font-13-600-grey">
                            {row?.status}
                          </TableCell>
                          <TableCell className="font-13-600-grey">
                            {row?.disposition}
                          </TableCell>
                          <TableCell className="font-13-500">
                            {row?.calls}
                          </TableCell>
                          <TableCell className="font-13-500">
                            <Box className="flex-align-center" gap={1}>
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {row?.percent?.toFixed(2)}%
                              </Typography>
                              <Box
                                sx={{
                                  width: 60,
                                  height: 8,
                                  backgroundColor: row.bg,
                                  borderRadius: 4,
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${row?.percent}%`,
                                    height: "100%",
                                    backgroundColor: row.color,
                                    transition: "width 0.3s ease",
                                  }}
                                />
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <Typography className="font-13-500">
                            No disposition data available for this gateway.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <>
          {searchField(
            projectSearch,
            setProjectSearch,
            "Search project name or id…"
          )}
          <DynamicTable
            columns={[
              {
                label: "Project Name",
                field: "projectName",
                render: (row: any) => (
                  <a
                    href={`/account/project/${row?.projectId}/call-settings`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open project in a new tab"
                    style={{ color: "#2563EB", textDecoration: "underline" }}
                  >
                    {shortenString(row?.projectName || "-", 45)}
                  </a>
                ),
              },
              { label: "Project Id", field: "projectId" },
              {
                label: "Gateway",
                field: "gatewayName",
                render: (row: any) => {
                  const name = gatewayNameFor(row?.gateway);
                  return (
                    <span
                      className="font-13-600-grey"
                      title={name}
                      style={{
                        display: "inline-block",
                        maxWidth: 190,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "middle",
                      }}
                    >
                      {name}
                    </span>
                  );
                },
              },
              {
                label: "Total Calls",
                field: "totalCalls",
                render: (row: any) =>
                  row?.totalCalls === undefined ? (
                    <LoadingDots font="14px" />
                  ) : (
                    <span className="font-13-600-grey">{row?.totalCalls}</span>
                  ),
              },
              {
                label: "Connected Calls",
                field: "connectedCalls",
                render: (row: any) =>
                  row?.connectedCalls === undefined ? (
                    <LoadingDots font="14px" />
                  ) : (
                    <div
                      style={{
                        background: "#22C55E",
                        color: "#fff",
                        padding: "4px 10px",
                        borderRadius: 4,
                        display: "inline-block",
                      }}
                    >
                      {row?.connectedCalls}
                    </div>
                  ),
              },
              {
                label: "Action",
                render: (row: any) => {
                  const pending =
                    pendingGatewayById[row?.projectId] ?? row?.gateway;
                  const changed = !!pending && pending !== row?.gateway;
                  const saving = savingProjectId === row?.projectId;
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "nowrap",
                      }}
                    >
                      <Box sx={{ width: 260, flexShrink: 0 }}>
                        <SearchableSelect
                          value={pending || ""}
                          setValue={(val: string) =>
                            setPendingGateway(row?.projectId, val)
                          }
                          options={gatewayOptions}
                          placeholder="Select gateway"
                        />
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        disableElevation
                        disabled={!changed || saving}
                        onClick={() => handleSaveGateway(row)}
                        sx={{
                          textTransform: "none",
                          minWidth: 56,
                          flexShrink: 0,
                          px: 1.5,
                        }}
                      >
                        {saving ? <LoadingDots font="13px" /> : "Save"}
                      </Button>
                    </Box>
                  );
                },
              },
            ]}
            data={pagedProjectRows}
            loading={loading}
            rowCount={pagedProjectRows?.length}
            totalRowCount={filteredProjectRows?.length}
            currentPage={projectPage}
            totalPages={projectTotalPages}
            handlePageChange={(_: any, p: number) => setProjectPage(p)}
            handlePrevious={() =>
              setProjectPage((p) => Math.max(1, p - 1))
            }
            handleNext={() =>
              setProjectPage((p) => Math.min(projectTotalPages, p + 1))
            }
            noDataAvailableMessage="No projects found"
          />
        </>
      )}
    </Box>
  );
}

export default GatewayPerformance;
